// src/services/apaleo/invoices.ts
// Extracted verbatim from src/services/apaleo.ts (Phase 5 of
// plans/monolith-migration-plan.md). `this` is typed via ThisType<ApaleoClient>.

import i18n from "../../i18n";
import type { ApaleoClient } from './client-contract';
import { errorMessage } from './config';
import { tokenGetterFn } from './client';

const part: ThisType<ApaleoClient> = {
async fetchInvoicePdf(invoiceId: string): Promise<Blob | null> {
  const res = (await this._fetch(`/finance/v1/invoices/${invoiceId}/pdf`, {
    headers: { Accept: "application/pdf" },
    raw: true,
  })) as Response | null;
  if (!res || !res.ok) return null;
  return await res.blob();
},

async fetchInvoiceData(invoiceId: string): Promise<unknown> {
  return await this._fetch(`/finance/v1/invoices/${invoiceId}`);
},

async cancelInvoice(
  invoiceId: string,
  reasonCode = "ChangeOfRecipientDetails",
): Promise<unknown> {
  try {
    return await this._fetch(`/finance/v1/invoice-actions/${invoiceId}/cancel`, {
      method: "PUT",
      body: JSON.stringify({ reasonCode }),
    });
  } catch (e) {
    throw new Error(`${i18n.t('common.error')} ${invoiceId}: ${errorMessage(e)}`);
  }
},

async patchFolio(
  folioId: string,
  patchOps: Record<string, unknown>[],
): Promise<unknown> {
  try {
    return await this._fetch(`/finance/v1/folios/${folioId}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json-patch+json" },
      body: JSON.stringify(patchOps),
    });
  } catch (e) {
    throw new Error(`${i18n.t('common.error')} ${folioId}: ${errorMessage(e)}`);
  }
},

async createInvoice(folioId: string, languageCode = "de"): Promise<unknown> {
  try {
    return await this._fetch(`/finance/v1/invoices`, {
      method: "POST",
      body: JSON.stringify({ folioId, languageCode }),
    });
  } catch (e) {
    throw new Error(`${i18n.t('common.error')}: ${errorMessage(e)}`);
  }
},

async registerApaleoWebhook(domain: string | null, secret = "") {
  void secret; // accepted for signature compatibility, deliberately unused
  const activeDomain = domain || (typeof window !== "undefined" ? window.location.origin : "");
  if (!activeDomain) {
    console.error(
      "Bitte gib deine AdaptHub-Domain an (z.B. https://www.adapthub.de)",
    );
    return;
  }
  const cleanDomain = activeDomain.replace(/\/$/, ""); // Strip trailing slash
  const targetUrl = `${cleanDomain}/api/webhook-apaleo-v2`;

  console.log(
    `%c[Webhook Registration] Proxying registration request via Vercel backend...`,
    "color: #859F4A; font-weight: bold;",
  );
  try {
    const tokenHeaders: Record<string, string> = {};
    if (tokenGetterFn) {
      const authToken = await tokenGetterFn();
      if (authToken) {
        tokenHeaders["Authorization"] = `Bearer ${authToken}`;
      }
    }

    const response = await fetch("/api/register-webhook", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...tokenHeaders,
      },
      body: JSON.stringify({
        endpointUrl: targetUrl,
      }),
    });

    const data = (await response.json()) as { error?: unknown };
    if (!response.ok) {
      console.error("[Webhook Registration] Backend error response:", data);
      throw new Error(errorMessage(data.error) || "Backend registration failed");
    }

    console.log(
      "%c[Webhook Registration] Successfully subscribed to topic 'reservation' in the cloud!",
      "color: green; font-weight: bold;",
      data,
    );
    return data;
  } catch (error) {
    console.error(
      "[Webhook Registration] Error subscribing to webhook:",
      error,
    );
  }
},

};

export default part;
